import type { LoginSuccess } from '../api/auth';
import type { AuthSession, AuthenticatedUser, PendingEmailChange, SessionTokens } from '../store/auth-store';

export const normalizeUser = (raw?: Record<string, unknown>): AuthenticatedUser | null => {
  if (!raw) {
    return null;
  }

  const user: AuthenticatedUser = {
    id: String(raw.id ?? ''),
    username: String(raw.username ?? raw.username_canonical ?? ''),
    email: String(raw.email ?? ''),
    role: (raw.role as string | undefined) ?? null,
    isRootAdmin: typeof raw.is_root_admin === 'boolean' ? (raw.is_root_admin as boolean) : undefined,
    lastLoginAt: typeof raw.last_login_at === 'string' ? (raw.last_login_at as string) : null,
    subscriptionTier: (raw.subscription_tier as string | undefined) ?? null,
    subscriptionStatus: (raw.subscription_status as string | undefined) ?? null,
    setupState: (raw.setup_state as AuthenticatedUser['setupState'] | undefined) ?? null,
    marketingOptIn: typeof raw.marketing_opt_in === 'boolean'
      ? (raw.marketing_opt_in as boolean)
      : (typeof raw.marketingOptIn === 'boolean' ? (raw.marketingOptIn as boolean) : undefined),
    marketingOptInDate: typeof raw.marketing_opt_in_date === 'string'
      ? (raw.marketing_opt_in_date as string)
      : (typeof raw.marketingOptInDate === 'string' ? (raw.marketingOptInDate as string) : null),
    timezone: (raw.timezone as string | undefined) ?? null,
    requiresConsent: (raw.requiresConsent as boolean | undefined) ?? undefined,
    restrictedAccess: (raw.restrictedAccess as boolean | undefined) ?? undefined,
    pendingConsents: Array.isArray(raw.pendingConsents)
      ? (raw.pendingConsents as string[])
      : undefined,
    emailVerified: typeof raw.email_verified === 'boolean' ? (raw.email_verified as boolean) : undefined,
    pendingEmailChange: null,
    pendingCheckoutIntent: null,
  };

  const pendingCheckoutRaw = raw.pending_checkout_intent;
  if (pendingCheckoutRaw && typeof pendingCheckoutRaw === 'object') {
    const candidate = pendingCheckoutRaw as Record<string, unknown>;
    const tier = typeof candidate.tier === 'string' ? candidate.tier.toLowerCase() : '';
    if (tier === 'creator' || tier === 'pro' || tier === 'business') {
      user.pendingCheckoutIntent = {
        tier,
        billingCycle: candidate.billingCycle === 'annual' ? 'annual' : 'monthly',
        source: candidate.source === 'signup' ? 'signup' : 'upgrade',
        intentOnly: candidate.intentOnly === true,
        sessionStatus: typeof candidate.sessionStatus === 'string' ? candidate.sessionStatus : null,
        trial: candidate.trial === true,
      };
    }
  }

  const pendingChangeRaw = raw.pending_email_change;
  if (pendingChangeRaw && typeof pendingChangeRaw === 'object') {
    const candidate = pendingChangeRaw as Record<string, unknown>;
    const pendingChange: PendingEmailChange = {
      id: String(candidate.id ?? ''),
      previousEmail: String(candidate.previous_email ?? candidate.previousEmail ?? ''),
      newEmail: String(candidate.new_email ?? candidate.newEmail ?? ''),
      status: String(candidate.status ?? 'pending') as PendingEmailChange['status'],
      createdAt: typeof candidate.created_at === 'string' ? candidate.created_at : null,
      resolvedAt: typeof candidate.resolved_at === 'string' ? candidate.resolved_at : null,
    };

    user.pendingEmailChange = pendingChange;
  }

  return user;
};

export const buildSessionFromLogin = (payload: LoginSuccess): AuthSession | null => {
  const accessToken = payload.accessToken ?? null;
  const idToken = payload.token ?? null;

  if (!accessToken || !idToken) {
    console.warn('[auth] missing tokens', { hasAccess: Boolean(accessToken), hasId: Boolean(idToken) });
  }

  if (!accessToken || !idToken) {
    return null;
  }

  const tokens: SessionTokens = {
    accessToken,
    idToken,
    refreshToken: payload.refreshToken,
    expiresAt: payload.expiresIn ? Date.now() + payload.expiresIn * 1000 : undefined,
    deviceKey: payload.deviceKey ?? null,
    deviceGroupKey: payload.deviceGroupKey ?? null,
  };

  const rawUser = payload.user as Record<string, unknown> | undefined;
  const user = normalizeUser(rawUser) ?? null;

  if (user) {
    if (typeof payload.requiresConsent === 'boolean') {
      user.requiresConsent = payload.requiresConsent;
    }
    if (typeof payload.restrictedAccess === 'boolean') {
      user.restrictedAccess = payload.restrictedAccess;
    }
    if (Array.isArray(payload.pendingConsents)) {
      user.pendingConsents = payload.pendingConsents;
    }
  }

  return {
    tokens,
    user,
    sessionId: payload.sessionId ?? null,
  };
};
